import { growthFactor, type Timing } from "@/lib/tool-kit/finance/tvm";

/**
 * Year-by-year growth of a balance with regular deposits.
 *
 * Convention: the balance grows over any span of `t` years by (1 + r/m)^(m·t), the
 * compounding schedule extended smoothly to fractional periods (e^(r·t) for continuous
 * compounding). When deposits fall on the compounding dates this is exactly the textbook
 * future-value-of-an-annuity formula. When deposits are more frequent than compounding,
 * a bank that credits interest only on its compounding dates would show slightly less.
 */

export interface CompoundInput {
  principal: number;
  /** Nominal annual rate as a decimal. */
  annualRate: number;
  /** Compounding periods per year; `CONTINUOUS` for continuous. */
  perYear: number;
  years: number;
  /** Amount deposited each deposit date; 0 for none. */
  deposit: number;
  depositsPerYear: number;
  timing: Timing;
}

export interface CompoundRow {
  /** Elapsed years at the end of the row (a whole number except for the last row). */
  year: number;
  deposits: number;
  interest: number;
  /** Principal plus every deposit made so far. */
  contributed: number;
  balance: number;
}

export interface CompoundResult {
  rows: CompoundRow[];
  finalBalance: number;
  totalDeposits: number;
  totalInterest: number;
  depositCount: number;
}

const EPSILON = 1e-9;

export function compoundSchedule(input: CompoundInput): CompoundResult {
  const { principal, annualRate, perYear, years, deposit, timing } = input;
  const k = deposit > 0 ? Math.max(1, Math.round(input.depositsPerYear)) : 1;
  const count = Math.floor(years * k + EPSILON);
  const step = growthFactor(annualRate, perYear, 1 / k);
  const rows: CompoundRow[] = [];
  let balance = principal;
  let made = 0;
  let lastBalance = principal;
  let lastMade = 0;

  const record = (year: number) => {
    const deposits = (made - lastMade) * deposit;
    rows.push({
      year,
      deposits,
      interest: balance - lastBalance - deposits,
      contributed: principal + made * deposit,
      balance,
    });
    lastBalance = balance;
    lastMade = made;
  };

  for (let j = 1; j <= count; j += 1) {
    if (timing === "begin") {
      balance += deposit;
      made += 1;
      balance *= step;
    } else {
      balance *= step;
      balance += deposit;
      made += 1;
    }
    if (j % k === 0) {
      record(j / k);
    }
  }
  const stub = years - count / k;
  if (stub > EPSILON) {
    balance *= growthFactor(annualRate, perYear, stub);
  }
  if ((rows.at(-1)?.year ?? 0) < years - EPSILON) {
    record(years);
  }
  return {
    rows,
    finalBalance: balance,
    totalDeposits: made * deposit,
    totalInterest: balance - principal - made * deposit,
    depositCount: made,
  };
}

/** The same balance in closed form — used by the tests as an independent check. */
export function compoundClosedForm(input: CompoundInput): number {
  const { principal, annualRate, perYear, years, deposit, timing } = input;
  const k = deposit > 0 ? Math.max(1, Math.round(input.depositsPerYear)) : 1;
  const count = Math.floor(years * k + EPSILON);
  const step = growthFactor(annualRate, perYear, 1 / k);
  const stubGrowth = growthFactor(annualRate, perYear, years - count / k);
  let annuity = count * deposit;
  if (step !== 1) {
    annuity = (deposit * (step ** count - 1)) / (step - 1);
  }
  if (timing === "begin") {
    annuity *= step;
  }
  return (principal * step ** count + annuity) * stubGrowth;
}
